/**
 * A densidade das estações no mapa, em grade (MV2-1706).
 *
 * Existe porque o Heatmap Layer do Google **foi removido** da Maps JavaScript API na
 * versão 3.65 — "The Heatmap Layer functionality in the Maps JavaScript API is no longer
 * available as of version 3.65" — e o @react-google-maps/api pede `v=weekly`, que já
 * passou disso. Não há opção, biblioteca ou ordem de carregamento que traga a camada de
 * volta; o que dá para fazer é desenhar a densidade com o que continua existindo.
 *
 * A grade usa só o núcleo da API (retângulo e cor), então não depende de biblioteca
 * opcional nem de versão. E responde melhor à pergunta "onde tem mais": cada célula tem
 * contagem, coisa que um borrão não tem.
 *
 * Tudo aqui é função pura, para a regra ficar testável sem mapa nenhum.
 */

export type HeatPoint = {
  lat: number;
  lng: number;
};

export type HeatCell = {
  south: number;
  west: number;
  north: number;
  east: number;

  /** Quantas estações caíram na célula. */
  count: number;

  /** 0 a 1, relativo à célula mais cheia — é o que define a cor. */
  intensity: number;
};

/**
 * O lado da célula em graus, pelo nível de zoom.
 *
 * Uma célula deve valer sempre mais ou menos o mesmo em pixels, senão a leitura muda de
 * significado quando a pessoa dá zoom: perto vira mancha única, longe vira poeira. O mundo
 * tem 256·2^zoom pixels de largura, então 40 px equivalem a 360·40/(256·2^zoom) graus —
 * que é o 56,25/2^zoom abaixo.
 *
 * Limitado nas duas pontas: célula gigante vira uma cor só na tela, e célula microscópica
 * vira um ponto por estação, que é a lista com passos extras.
 */
export const heatCellSize = (zoom: number): number =>
  Math.min(45, Math.max(0.0005, 56.25 / 2 ** Math.max(0, zoom)));

/**
 * Agrupa os pontos em células e devolve as não vazias, da mais cheia para a mais vazia.
 *
 * A ordem importa no desenho: as células são translúcidas, e desenhar a mais cheia por
 * último a esconderia sob as vizinhas.
 */
export const heatCells = (points: HeatPoint[], zoom: number): HeatCell[] => {
  if (!points || points.length === 0) {
    return [];
  }

  const size = heatCellSize(zoom);
  const buckets = new Map<string, { latIndex: number; lngIndex: number; count: number }>();

  points.forEach((point) => {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
      return;
    }

    const latIndex = Math.floor(point.lat / size);
    const lngIndex = Math.floor(point.lng / size);
    const key = `${latIndex}:${lngIndex}`;
    const found = buckets.get(key);

    if (found) {
      found.count += 1;

      return;
    }

    buckets.set(key, { latIndex, lngIndex, count: 1 });
  });

  const max = Array.from(buckets.values()).reduce(
    (biggest, bucket) => Math.max(biggest, bucket.count),
    0,
  );

  return Array.from(buckets.values())
    .map((bucket) => ({
      south: bucket.latIndex * size,
      west: bucket.lngIndex * size,
      north: (bucket.latIndex + 1) * size,
      east: (bucket.lngIndex + 1) * size,
      count: bucket.count,
      intensity: max === 0 ? 0 : bucket.count / max,
    }))
    .sort((a, b) => b.count - a.count);
};

/**
 * A cor da célula, na mesma escala azul e violeta do resto da aba.
 *
 * Não usa vermelho de propósito: vermelho neste módulo significa "o parceiro não consegue
 * usar", e a célula mais cheia passaria a mensagem oposta.
 */
export const heatCellColor = (intensity: number): string => {
  if (intensity >= 0.8) {
    return '#5b3ba8';
  }

  if (intensity >= 0.55) {
    return '#7a56d1';
  }

  if (intensity >= 0.3) {
    return '#3f6ed0';
  }

  return '#2f9fd0';
};

/** Translucidez pela intensidade: célula fraca deixa o mapa aparecer por baixo. */
export const heatCellOpacity = (intensity: number): number =>
  Math.min(0.62, 0.18 + intensity * 0.44);

export type HeatCircle = {
  center: { lat: number; lng: number };

  /** Em metros, que é o que o Circle do Google recebe. */
  radius: number;

  color: string;
  opacity: number;
  zIndex: number;

  /** Contagem da célula de origem, para o teste e para depuração. */
  count: number;
};

/** Um grau de latitude, em metros. Serve de escala para o raio. */
const METERS_PER_DEGREE = 111_320;

/**
 * Quantos anéis por célula, e o tamanho de cada um em relação à célula.
 *
 * Um círculo só corta seco na borda — foi o que ficou estranho no retângulo e continuaria
 * estranho com um círculo. Três anéis concêntricos, do mais largo e translúcido ao mais
 * estreito e forte, fazem a intensidade cair devagar, e onde as células são vizinhas os
 * anéis de fora se sobrepõem e emendam. É um degradê radial pobre, feito com o que a API
 * tem: o de verdade morreu com o Heatmap Layer.
 */
const RINGS = [
  { scale: 0.95, weight: 0.34 },
  { scale: 0.62, weight: 0.6 },
  { scale: 0.34, weight: 1 },
];

/**
 * Converte as células em círculos concêntricos, na ordem de desenho.
 *
 * A ordem é do anel de fora para o de dentro: o de dentro precisa ficar por cima, senão o
 * translúcido de fora o lava.
 */
export const heatCircles = (cells: HeatCell[]): HeatCircle[] => {
  if (!cells || cells.length === 0) {
    return [];
  }

  return cells.flatMap((cell) => {
    const height = cell.north - cell.south;

    // O raio base passa um pouco do meio da célula, para vizinhas se tocarem em vez de
    // deixar buraco entre elas.
    const base = (height / 2) * 1.25 * METERS_PER_DEGREE;

    return RINGS.map((ring, index) => ({
      center: { lat: (cell.south + cell.north) / 2, lng: (cell.west + cell.east) / 2 },
      radius: base * ring.scale,
      color: heatCellColor(cell.intensity),
      opacity: heatCellOpacity(cell.intensity) * ring.weight,
      zIndex: 1 + index,
      count: cell.count,
    }));
  });
};
